import React, { useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";
import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";

const ACCENT = "#2563eb";
const ACCENT_DARK = "#1d4ed8";
const TEXT_TITLE = "#0f172a";
const TEXT_PLACEHOLDER = "#94a3b8";
const FIELD_BG = "#f1f5f9";
const ERROR = "#dc2626";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
    padding: theme.spacing(2, 2.5),
    "& h2": {
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightSemiBold,
      fontSize: "1rem !important",
      color: "white",
      paddingTop: 3,
    },
  },
  content: {
    padding: theme.spacing(2.5),
    paddingBottom: 0,
    fontFamily: theme.typography.fontFamily,
  },
  fieldGrid: {
    width: "100%",
    margin: 0,
    "& > .MuiGrid-item": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
    "& > .MuiGrid-item:nth-last-child(-n+1)": {
      marginBottom: 0,
    },
  },
  fieldGridItem: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
    marginBottom: theme.spacing(1.25),
  },
  fieldGridItemLeft: {
    paddingRight: `${theme.spacing(1)}px !important`,
  },
  fieldGridItemRight: {
    paddingLeft: `${theme.spacing(1)}px !important`,
  },
  fieldLabel: {
    color: TEXT_TITLE,
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    marginBottom: 2,
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.1,
  },
  required: {
    color: ERROR,
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  field: {
    margin: "0 !important",
    "&.MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: FIELD_BG,
      border: "1px solid transparent",
      fontFamily: theme.typography.fontFamily,
      padding: "0 !important",
      transition:
        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        backgroundColor: "#e9eef5",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        borderColor: ACCENT,
        boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.15)`,
      },
      "&.Mui-error": {
        borderColor: ERROR,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px rgba(220, 38, 38, 0.12)`,
      },
      "&::before, &::after": {
        display: "none",
        content: "none",
        border: "none",
      },
    },
    "& .MuiFilledInput-input, & .MuiInputBase-input, & .MuiInputBase-inputHiddenLabel": {
      padding: "14px 12px !important",
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.85rem !important",
      fontWeight: theme.typography.fontWeightRegular,
      color: TEXT_TITLE,
      "&::placeholder": {
        color: TEXT_PLACEHOLDER,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightRegular,
        opacity: 1,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 2,
      marginTop: 2,
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.7rem !important",
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  selectMenuPaper: {
    marginTop: 4,
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
    maxHeight: 240,
    backgroundColor: "white",
    "& .MuiMenuItem-root": {
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.82rem !important",
      minHeight: "auto",
      padding: theme.spacing(0.75, 1.5),
    },
    "& .MuiList-padding": {
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
  actions: {
    padding: theme.spacing(1, 2.5, 1.5, 2.5),
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    "& > :not(:first-child)": {
      marginLeft: 0,
    },
  },
  primaryBtn: {
    textTransform: "none",
    backgroundColor: "#1d4ed8",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    fontSize: "0.85rem !important",
    "&:hover": {
      backgroundColor: "#1e40af",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "1px solid #cbd5e1",
    },
  },
  cancelBtn: {
    color: TEXT_TITLE,
    backgroundColor: "white",
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightSemiBold,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: "#f8fafc",
      border: "1px solid #94a3b8",
    },
  },
}));

const AddUser = ({ onClose, open = true }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [firstNameHelper, setFirstNameHelper] = useState("");
  const [lastNameHelper, setLastNameHelper] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailHelper, setEmailHelper] = useState("");
  const [role, setRole] = useState("");
  const [RoleData, setRoleData] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [makeSelected] = useContext(Context);

  const userRole = useSelector((state) => {
    return state?.OAuth?.authData?.role;
  });

  React.useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setRole("");
      setFirstNameHelper("");
      setLastNameHelper("");
      setEmailHelper("");
      setSubmitting(false);
      return;
    }

    const loadRoles = async () => {
      if (userRole !== undefined) {
        await dispatch(OAuthActions.getRole()).then(function (roleData) {
          if (roleData) {
            if (userRole === "SUPER_ADMIN") {
              setRoleData(
                roleData.filter(
                  (roleDetails) =>
                    roleDetails.name !== "Super Admin" &&
                    roleDetails.name !== "Utility Admin"
                )
              );
            } else if (userRole === "ADMIN") {
              setRoleData(
                roleData.filter(
                  (roleDetails) =>
                    roleDetails.name.indexOf("Admin") === -1
                )
              );
            }
          }
        });
      }
    };

    loadRoles();
  }, [userRole, makeSelected, dispatch, open]);

  const canSubmit =
    firstName !== "" &&
    lastName !== "" &&
    email !== "" &&
    role !== "" &&
    emailHelper.length === 0 &&
    firstNameHelper.length === 0 &&
    lastNameHelper.length === 0 &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const success = await dispatch(
        OAuthActions.createUser(
          firstName,
          lastName,
          email,
          role,
          makeSelected.uid
        )
      );

      if (success?.error === undefined) {
        dispatch(
          setSnackbar(
            true,
            "success",
            "Add User Successful"
          )
        );
        onClose();
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Add User",
            "Add user unsuccessful.\nDetails Of Failure: " +
            success.message,
            "OK",
            ""
          )
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "Unable to add user. Please try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6" component="h2" style={{ fontWeight: 700 }}>
          Add User
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2} className={classes.fieldGrid}>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              First Name <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);

                const valid = /^[a-zA-Z\s]+$/.test(
                  e.target.value
                );

                if (
                  !valid &&
                  e.target.value.length !== 0
                ) {
                  setFirstNameHelper(
                    "Name contains only Alphabets"
                  );
                } else {
                  setFirstNameHelper("");
                }
              }}
              error={firstNameHelper.length !== 0}
              helperText={firstNameHelper}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>
              Last Name <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);

                const valid = /^[a-zA-Z\s]+$/.test(
                  e.target.value
                );

                if (
                  !valid &&
                  e.target.value.length !== 0
                ) {
                  setLastNameHelper(
                    "Name contains only Alphabets"
                  );
                } else {
                  setLastNameHelper("");
                }
              }}
              error={lastNameHelper.length !== 0}
              helperText={lastNameHelper}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              Role <span className={classes.required}>*</span>
            </div>
            <Autocomplete
              fullWidth
              options={RoleData}
              getOptionLabel={(option) => option.name || ""}
              value={
                role
                  ? RoleData.find((r) => r.uid === role) || null
                  : null
              }
              onChange={(event, newValue) => {
                if (newValue == null) {
                  setRole("");
                } else {
                  setRole(newValue.uid);
                }
              }}
              noOptionsText="No roles available"
              disableClearable={true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  hiddenLabel
                  className={classes.field}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
              )}
              ListboxProps={{
                style: { maxHeight: "300px", overflowY: "auto" },
              }}
              PaperComponent={(props) => (
                <div
                  {...props}
                  className={classes.selectMenuPaper}
                  style={{
                    ...props.style,
                    maxHeight: 300,
                  }}
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>
              Email ID <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={email}
              onChange={(event) => {
                const valid =
                  /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(
                    event.target.value
                  );

                if (
                  !valid &&
                  event.target.value.length !== 0
                ) {
                  setEmailHelper("Invalid email address");
                } else {
                  setEmailHelper("");
                }

                setEmail(event.target.value);
              }}
              error={emailHelper.length !== 0}
              helperText={emailHelper}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          className={classes.cancelBtn}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          className={classes.primaryBtn}
          variant="contained"
          disableElevation
          onClick={submit}
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} style={{ color: "white" }} />
            ) : null
          }
        >
          {submitting ? "Creating..." : "Register"}
        </Button>
      </DialogActions>
    </>
  );
};

export default AddUser;